import { Params } from '@angular/router';
import { AccessTokenFormat as AccessTokenFormatReact, AccessTokenSource as AccessTokenSourceReact } from '@office-start/auth-types';
import { NetworkType as NetworkTypeReact, ServiceWorkerState as ServiceWorkerStateReact } from '@office-start/common-types';
import { initializeComponentContext } from '@office-start/component-context-service';
import { Document as OfficeStartDocument, FileType as OfficeStartFileType } from '@office-start/document-types';
import { DataSecurityLevel as DataSecurityLevelReact } from '@office-start/instrumentation-types';
import { OfficeComponentContext, OfficeEnvironment, OfficeRoute as OfficeStartRoute, OfficeRouteItem as OfficeStartRouteItem, OfficeWorkload } from '@office-start/officehome-types';
import { initializeOTelTelemetryService } from '@office-start/telemetry-service';
import { AppName, AppPlatform, BrowserHost, Geo, RequestOrigin, TelemetryNamespace, UserSegment } from '@office-start/telemetry-types';

import { FluidHostContext } from '../../fluid-content/scopes/fluid-host-context';
import { FluidInstrumentationService } from '../../fluid-content/utils/fluid-instrumentation-service';
import { EncryptionConfigService } from '../../service-worker/encryption/encryption-config.service';
import { ServiceWorkerStateService } from '../../service-worker/service-worker-state.service';
import { OfficeRoute } from '../constants/office-route.enum';
import { RoutePaths } from '../constants/route-paths.constant';
import { WorkloadQueryParameter } from '../constants/workload-query-parameter.enum';
import { AriaEventFactoryService } from '../instrumentation/aria.service';
import { InstrumentationParams, KnownInstrumentationEvents } from '../instrumentation/instrumentation.service';
import { UserProfileService } from '../instrumentation/user-profile.service';
import { FileType } from '../models/file-item.model';
import { ApplicationUrlParam } from '../models/jsapi.model';
import { WdOriginApp } from '../models/telemetry-constants.model';
import { DocumentInfo, DocumentQueryParams } from '../models/wac-host.model';
import { AadAppId } from '../services/aad-app-id.injection-token';
import { AccessTokenConsumer, AccessTokenService } from '../services/access-token.service';
import { DataSecurityService } from '../services/data-security.service';
import { DeviceDetector } from '../services/device-detector.service';
import { OfficeRouteItem, OfficeRouteService } from '../services/office-route.service';
import { OfficeHomeApiConfigService } from '../services/officehome-api-config.service';
import { PerformanceService } from '../services/performance.service';
import { SchedulePriority, SchedulerService } from '../services/scheduler.service';
import { ThemingService } from '../services/theming.service';
import { WacContentService } from '../services/wac-content.service';
import { AuthType, ClientStartupConfigService } from '../shared-context/client-startup-config.service';
import { ContextService } from '../shared-context/context.service';
import { UserContextService } from '../shared-context/user-context.service';

let bootstrapped = false;

// Common initialization logic required to run office-start service-backed code
// TODO: Consider exposing a single bootstrap function from office-start rather than separate bootstrappers for logging/icons/etc
export function bootstrapOfficeStart(
    aadAppId: AadAppId,
    accessTokenService: AccessTokenService,
    contextService: ContextService,
    fluidHostContext: FluidHostContext,
    fluidInstrumentationService: FluidInstrumentationService,
    officeHomeApiConfigService: OfficeHomeApiConfigService,
    officeRouteService: OfficeRouteService,
    startupConfigService: ClientStartupConfigService,
    performanceService: PerformanceService,
    ariaEventFactoryService: AriaEventFactoryService,
    serviceWorkerStateService: ServiceWorkerStateService,
    scheduler: SchedulerService,
    dataSecurityService: DataSecurityService,
    deviceDetector: DeviceDetector,
    themingService: ThemingService,
    wacContentService: WacContentService,
    workloadQueryParameterValue: WorkloadQueryParameter,
    userContextService: UserContextService,
    userProfileService: UserProfileService,
    encryptionConfigService?: EncryptionConfigService
): OfficeComponentContext {
    const context: OfficeComponentContext = {
        aadAppId,
        buildVersion: process.env.BUILD_ID,
        cdnDomain: startupConfigService.config.officeStartCdnDomain,
        correlationId: startupConfigService.config.sharedClientStartupContext.correlationId,
        defaultLinkTarget: contextService.sharedContext.defaultLinkTarget,
        deploymentEnvironment: startupConfigService.config.sharedClientStartupContext.deploymentEnvironment as OfficeEnvironment,
        geoName: startupConfigService.config.sharedClientStartupContext.geoName,
        getAccessToken: (consumer, forceFetch, resourceUrl, scope) => accessTokenService.getAccessToken(consumer as string as AccessTokenConsumer, forceFetch, resourceUrl, undefined, undefined, undefined, scope).then(token =>
        ({
            success: token.success,
            token: token.token,
            format: token.format as string as AccessTokenFormatReact,
            source: token.source as string as AccessTokenSourceReact,
            expiryMs: token.expiryMs,
            error: token.error,
            clientId: token.clientId,
            scope: token.scope
        })),
        getIdentity: () => Promise.resolve({
            isMsa: startupConfigService.config.authType === AuthType.MSA,
            objectId: startupConfigService.config.objectId,
            puid: startupConfigService.config.puid,
            tenantId: startupConfigService.config.tenantId,
            encryptionConfig: encryptionConfigService?.config,
            userPrincipalName: startupConfigService.config.userPrincipalName
        }),
        inMockMode: contextService.sharedContext.inMockMode,
        isCorpNet: startupConfigService.config.sharedClientStartupContext.corpNet,
        jwtAuthEnabled: officeHomeApiConfigService.config.jwtAuthEnabled,
        officeHomeApiRoot: contextService.sharedContext.officeHomeApiRoot,
        officeStartManifestVersion: contextService.sharedContext.officeStartManifestVersion,
        preferredCulture: contextService.sharedContext.officeMarket,
        requestOrigin: startupConfigService.config.sharedClientStartupContext.requestOrigin,
        router: {
            getCurrentRoute: () => officeStartRouteFromOfficeHomeRoute(officeRouteService.getCurrentRoute()),
            getUrlForRoute: (route, additionalParams) => officeRouteService.getUrlForRoute(officeHomeRouteFromOfficeStartRoute(route), additionalParams),
            navigateTo: (route, replace, params, storageKey, eagerLoad) => {
                if (route.route === OfficeRoute.M365Apps as string as OfficeStartRoute) {
                    const m365route = RoutePaths.M365Apps;
                    return officeRouteService.navigateToPath(m365route, route.app);
                } else {
                    return officeRouteService.navigateTo(officeHomeRouteFromOfficeStartRoute(route), replace, params, storageKey, eagerLoad);
                }
            },
            openHostedDocument: (document, startTime, wdOrigin, fluid, fluidHostScenarioName, additionalParams) => {
                // TODO: This is probably temporary until we figure out how to bootstrap in office-start
                // This currently assumes only fluid or wac files
                if (fluid) {
                    fluidInstrumentationService.logPerformanceBootStart(
                        fluidHostContext.getBootId()
                    );
                    fluidHostContext.hostScenarioName = fluidHostScenarioName;
                    fluidHostContext.hostScenarioStartTime = startTime;
                    officeRouteService.navigateTo(
                        { route: OfficeRoute.Content, app: FileType.Fluid },
                        false,
                        WacContentService.getWacParams(
                            document.driveId,
                            document.docId,
                            document.canonicalUrl,
                            document.sharepointIds ? { siteUrl: document.sharepointIds.siteUrl } : undefined
                        )
                    );
                } else {
                    const nav = additionalParams?.[ApplicationUrlParam.Nav];
                    wacContentService.bootstrapAndRoute(
                        getDocumentInfoFromOfficeStartDocument(document, wdOrigin, additionalParams),
                        nav ? { [DocumentQueryParams.Nav]: nav } : undefined,
                        startTime
                    );
                }
                // TODO: Handle routing failures?
                return Promise.resolve();
            }
        },
        sessionId: startupConfigService.config.sharedClientStartupContext.sessionId,
        stringsContainerName: 'officestartstrings', // TODO: Move to office-start settings
        darkModeSubscription: themingService.darkModeSubscription,
        workload: workloadQueryParameterValue as string as OfficeWorkload,
        oneDriveUrl: contextService.sharedContext.oneDriveUrl
    };

    if (!bootstrapped) {
        bootstrapped = true;
        import(
            /*webpackChunkName: "instrumentation-service" */ '@office-start/instrumentation-service'
        ).then(mod => {
            mod.initializeInstrumentation(
                [
                    {
                        // Aria
                        logEvent: (streamName, params, metrics) => ariaEventFactoryService.createEvent(streamName, params as InstrumentationParams, metrics).record(),
                        flush: () => {
                            ariaEventFactoryService.flush();
                        }
                    }
                ],
                params => {
                    mod.applyCommonStateParams(
                        params,
                        officeRouteService.getInitialRoute() ? officeRouteService.getInitialRoute().route :  OfficeRoute.OfficeHome,
                        officeRouteService.getCurrentRoute() ? officeRouteService.getCurrentRoute().route : OfficeRoute.OfficeHome,
                        ServiceWorkerStateReact[serviceWorkerStateService.currentState],
                        DataSecurityLevelReact[dataSecurityService.securityLevel.toString()],
                        NetworkTypeReact[deviceDetector.getNetworkInfo()]
                    );
                }
            );
        });

        initializeComponentContext(context);

        scheduler.scheduleWork(() => {
            initializeOTelTelemetryService(
                startupConfigService.config.sharedClientStartupContext.oTelAppName as AppName,
                startupConfigService.config.sharedClientStartupContext.oTelAppPlatform as AppPlatform,
                getBrowserHost(contextService),
                startupConfigService.config.sharedClientStartupContext.buildDateUtc,
                startupConfigService.config.sharedClientStartupContext.buildId,
                context.correlationId,
                context.deploymentEnvironment,
                {
                    pageViewEnabled: startupConfigService.config.sharedClientStartupContext.oTelPageViewEnabled,
                    clickEnabled: startupConfigService.config.sharedClientStartupContext.oTelClickEnabled
                },
                {
                    isMsa: startupConfigService.config.authType === AuthType.MSA,
                    objectId: startupConfigService.config.objectId,
                    puid: startupConfigService.config.puid,
                    tenantId: startupConfigService.config.tenantId,
                    encryptionConfig: encryptionConfigService?.config,
                    userPrincipalName: startupConfigService.config.userPrincipalName
                },
                startupConfigService.config.sharedClientStartupContext.corpNet,
                startupConfigService.config.sharedClientStartupContext.oTelEnabled,
                startupConfigService.config.sharedClientStartupContext.oTelAriaTenant,
                startupConfigService.config.sharedClientStartupContext.oTelNameSpace as TelemetryNamespace,
                context.sessionId,
                startupConfigService.config.sharedClientStartupContext.geoName.toLowerCase() as Geo,
                context.requestOrigin as RequestOrigin,
                getUserSegment(userContextService, userProfileService),
                () => officeStartRouteFromOfficeHomeRoute(officeRouteService.getCurrentRoute())
            );
            performanceService.mark(KnownInstrumentationEvents.OtelReady);
        }, SchedulePriority.Low);
    }

    return context;
}

function officeHomeRouteFromOfficeStartRoute(officeStartRoute: OfficeStartRouteItem): OfficeRouteItem {
    return {
        route: officeStartRoute.route as string as OfficeRoute,
        app: FileType[officeStartRoute.app],
        entityId: officeStartRoute.entityId,
        subEntityId: officeStartRoute.subEntityId
    };
}

function officeStartRouteFromOfficeHomeRoute(officeHomeRoute: OfficeRouteItem): OfficeStartRouteItem {
    return {
        route: officeHomeRoute.route as OfficeStartRoute,
        app: FileType[officeHomeRoute.app] as OfficeStartFileType,
        entityId: officeHomeRoute.entityId,
        subEntityId: officeHomeRoute.subEntityId
    };
}

function getDocumentInfoFromOfficeStartDocument(document: OfficeStartDocument, wdOrigin: string, additionalParams: Params): DocumentInfo {
    return {
        docId: document.docId,
        docUrl: document.canonicalUrl,
        driveId: document.driveId,
        fileType: FileType[document.fileType],
        fileName: document.fileName,
        wdOrigin,
        applicationUrlParams: additionalParams
    };
}

function getUserSegment(userContextService: UserContextService, userProfileService: UserProfileService): UserSegment {
    return {
        isAdmin: userContextService?.user?.isAdmin,
        isDynamics: userContextService?.user?.isDynamics,
        isEdu: userContextService?.user?.isEdu,
        isEduFaculty: userContextService?.user?.isEduFaculty,
        isEduStudent: userContextService?.user?.isEduStudent,
        isFirstVisit: userProfileService?.isFirstVisit(),
        isIw: userContextService?.user?.isIw,
        isSmb: userContextService?.user?.isSmb
    };
}

function getBrowserHost(contextService: ContextService): BrowserHost {
    switch (contextService.getWdOriginApp()) {
        case WdOriginApp.Web:
            return 'Web';
        case WdOriginApp.HWA:
            return 'HWA';
        case WdOriginApp.PWA:
            return 'HWA';
        case WdOriginApp.NTP:
            return 'Edge';
        case WdOriginApp.Embed:
            return 'Embed';
        default:
            return 'Web';
    }
}
